import React from "react";

import moment from "moment";
import { TFunction, useTranslation } from "react-i18next";

import { Table } from "antd";
import { SortOrder } from "antd/es/table/interface";
import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { ColumnTitle } from "../../../../components/table";
import { ConversionBaseItem, ConversionItem } from "../../../../store/conversions/types";
import { percent } from "../../../../utils/percent";
import { formatNumber, getSortOrder } from "../../../../utils/table";

const fields = [
  "searches",
  "clicks",
  "uniqueClicks",
  "ctr",
  "bookings",
  "sales",
  "btr",
  "str",
  "productivity",
  "percentSuccessfulResponses",
  "percentErrorsResponses",
  "percentZerosResponses",
  "percentTimeouts",
  "responseDuration",
] as Array<keyof ConversionBaseItem>;

const extraFields = [
  "predictedSales",
  "successfulResponses",
  "errorsResponses",
  "zerosResponses",
  "timeouts",
] as Array<keyof ConversionBaseItem>;

const columns = (t: TFunction<"conversions_page">, ordering: string, timestampFormat: string) => {
  return [
    {
      title: <ColumnTitle title={t(`table.columns.time.title`)} helpText={t(`table.columns.time.help`)} />,
      dataIndex: "timestamp",
      key: "timestamp",
      render: (value: string) => moment(value).format(timestampFormat),
      sorter: true,
      sortOrder: getSortOrder("timestamp", ordering),
    },
    ...fields.map((field) => ({
      title: <ColumnTitle title={t(`table.columns.${field}.title`)} helpText={t(`table.columns.${field}.help`)} />,
      dataIndex: field,
      key: field,
      render: (value: number) => formatNumber(value),
      sorter: true,
      sortOrder: getSortOrder(field, ordering),
    })),
  ];
};

export interface ConversionsTableProps {
  timestampFormat: string;
  total: number;
  dataSource: ConversionItem[];
  ordering: string;
  currentPage: number;
  pageSize?: number;
  loading?: boolean;
  onChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<ConversionItem> | SorterResult<ConversionItem>[],
    extra: TableCurrentDataSource<ConversionItem>
  ) => void;
  summary?: ConversionBaseItem;
}

export const ConversionsTable: React.FC<ConversionsTableProps> = (props) => {
  const { t } = useTranslation("conversions_page");

  const calculateProductivity = (sales: number, predictedSales: number) => {
    if (predictedSales === 0 && sales > 0) {
      return 1.0;
    }

    if (predictedSales === 0 && sales === 0) {
      return 0.0;
    }

    return sales / predictedSales;
  }

  const summaryRows = (pageData: readonly ConversionItem[]) => {
    if (pageData.length === 0) {
      return <></>;
    }

    let pageSummary = pageData.reduce((summary, item) => {
      if (Object.keys(summary).length === 0) {
        return item;
      }

      return [...fields, ...extraFields].reduce(
        (obj, field) => ({ ...obj, [field]: summary[field] + item[field] }),
        {} as ConversionBaseItem
      );
    }, {} as ConversionBaseItem);

    pageSummary = {
      ...pageSummary,
      ctr: percent(pageSummary.clicks, pageSummary.searches),
      btr: percent(pageSummary.bookings, pageSummary.clicks),
      str: percent(pageSummary.sales, pageSummary.clicks),
      percentSuccessfulResponses: percent(pageSummary.successfulResponses, pageSummary.searches),
      percentErrorsResponses: percent(pageSummary.errorsResponses, pageSummary.searches),
      percentZerosResponses: percent(pageSummary.zerosResponses, pageSummary.searches),
      percentTimeouts: percent(pageSummary.timeouts, pageSummary.searches),
      responseDuration: pageSummary.responseDuration / pageData.length,
      productivity: calculateProductivity(pageSummary.sales, pageSummary.predictedSales),
    };

    return (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={1}>{t("table.summary.page")}</Table.Summary.Cell>
          {fields.map((field, idx) => {
            return (
              <Table.Summary.Cell key={`page-total-cell-${idx}`} index={idx + 1}>
                {formatNumber(pageSummary[field] as number)}
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
        <Table.Summary.Row>
          <Table.Summary.Cell index={101}>{t("table.summary.total")}</Table.Summary.Cell>
          {fields.map((field, idx) => {
            return (
              <Table.Summary.Cell key={`page-total-cell-${idx}`} index={idx + 1}>
                {props.summary && formatNumber(props.summary[field] as number)}
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
      </>
    );
  };

  return (
    <Table
      bordered
      columns={columns(t, props.ordering, props.timestampFormat)}
      size="small"
      dataSource={props.dataSource}
      loading={props.loading}
      rowKey={(record: ConversionItem) => `conversions-row-${record.timestamp}`}
      pagination={{
        total: props.total,
        current: props.currentPage,
        pageSize: props.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
      }}
      sortDirections={["ascend", "descend", "ascend"] as SortOrder[]}
      showSorterTooltip={false}
      onChange={props.onChange}
      summary={summaryRows}
    />
  );
};
